import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { PROPERTY_IDEAS_ON } from "@/lib/features";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, Send, X, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TOKEN_KEY, ADMIN_FIRM_KEY, api } from "@/lib/api";
import { AllyActionCard, parseAllyReply } from "@/components/AllyActionCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUGGESTIONS = [
  "Which client deadline is most urgent?",
  "Summarize my book of exchanges",
  "What's missing on my exchanges?",
];

const EXCHANGE_SUGGESTIONS = [
  "Summarize this exchange",
  "What's missing before the 45-day deadline?",
  "Did the client get our last update?",
];

const CLIENT_SUGGESTIONS = [
  "Explain my 45 and 180-day deadlines",
  "Which recommended listing fits me best?",
  "What happens if I buy for less than I sold?",
];

export const askAlly = (message) => window.dispatchEvent(new CustomEvent("ally:ask", { detail: { message } }));

const Inline = ({ text }) => text.split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g).map((part, i) =>
  part.startsWith("**") && part.endsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong>
    : part.length > 2 && part.startsWith("*") && part.endsWith("*") ? <em key={i}>{part.slice(1, -1)}</em> : <span key={i}>{part}</span>);

export const RichText = ({ text }) => text.split("\n").map((line, i) => {
  if (/^\s*-{3,}\s*$/.test(line)) return <hr key={i} className="my-2 border-black/10" />;
  if (/^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(line) && line.includes("-")) return null;
  if (/^\s*\|.*\|\s*$/.test(line)) line = line.trim().slice(1, -1).split("|").map((c) => c.trim()).join(" · ");
  const h = line.match(/^#{1,4}\s+(.*)/);
  line = line.replace(/^\s*>\s?/, "");
  return <div key={i} className={h ? "font-bold mt-1" : ""}>{line.trim() === "" ? "\u00A0" : <Inline text={h ? h[1] : line.replace(/^\s*[-*]\s+/, "• ")} />}</div>;
});

const newSessionId = () => "firm_" + Math.random().toString(36).slice(2) + Date.now().toString(36);

export const AllyWidget = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sid, setSid] = useState(newSessionId);
  const scrollRef = useRef(null);

  // Start a fresh conversation every time a user logs in (or the widget mounts).
  useEffect(() => {
    if (!user?.id) return;
    setSid(newSessionId());
    setMessages([]);
    setInput("");
    setOpen(false);
  }, [user?.id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming, open]);

  const isClient = PROPERTY_IDEAS_ON && user?.role === "client" && pathname.startsWith("/client/exchanges/");
  const onEnterprise = pathname.startsWith("/enterprise/");
  const exchangeId = (pathname.match(/^\/(?:enterprise|client)\/exchanges\/([^/]+)/) || [])[1] || null;
  const allowed = user && ["enterprise", "superadmin", "support", "support_viewer"].includes(user.role);
  const visible = isClient || (onEnterprise && allowed);
  const sendRef = useRef(null);
  useEffect(() => { if (open && exchangeId && allowed && onEnterprise) api.post(`/enterprise/exchanges/${exchangeId}/ally-prepare`).catch(() => {}); }, [open, exchangeId, allowed, onEnterprise]);
  useEffect(() => {
    const onAsk = (e) => { setOpen(true); setTimeout(() => sendRef.current?.(e.detail?.message), 50); };
    window.addEventListener("ally:ask", onAsk);
    return () => window.removeEventListener("ally:ask", onAsk);
  }, []);
  if (!visible) return null;

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ session_id: sid, message: content, exchange_id: exchangeId, firm_id: allowed && user?.role !== "enterprise" ? localStorage.getItem(ADMIN_FIRM_KEY) : null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Sorry, I had trouble connecting. Please try again.");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: acc }; return c; });
      }
    } catch (e) {
      const msg = e?.message && !e.message.startsWith("Failed") ? e.message : "Sorry, I had trouble connecting. Please try again.";
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: msg }; return c; });
    } finally { setStreaming(false); }
  };

  sendRef.current = send;

  const resetChat = () => {
    if (streaming) return;
    setSid(newSessionId());
    setMessages([]);
    setInput("");
  };

  return (
    <>
      <button data-testid="ally-launcher" onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[70] bg-ally-pink text-white rounded-full shadow-2xl w-14 h-14 flex items-center justify-center hover:scale-105 transition-transform">
        {open ? <X size={22} /> : <Sparkles size={22} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div data-testid="ally-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 z-[70] w-[380px] max-w-[calc(100vw-3rem)] h-[520px] bg-white rounded-[28px] shadow-2xl border border-black/5 flex flex-col overflow-hidden">
            <div className="px-5 py-4 border-b border-black/5 flex items-center gap-3 bg-ally-ink text-white">
              <div className="w-9 h-9 rounded-full bg-ally-pink flex items-center justify-center"><Sparkles size={16} /></div>
              <div>
                <div className="font-display font-bold leading-tight">Ally</div>
                <div className="text-xs text-white/60 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-400" /> {isClient ? <span data-testid="ally-client-focus">Your exchange guide</span> : exchangeId ? <span data-testid="ally-exchange-focus">Looking at this exchange</span> : "Your firm's 1031 copilot"}</div>
              </div>
              {messages.length > 0 && (
                <button data-testid="ally-new-chat" onClick={resetChat} disabled={streaming} title="Start a new chat"
                  className="ml-auto flex items-center gap-1.5 text-xs font-medium text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full px-3 py-1.5 transition-colors disabled:opacity-40">
                  <RotateCcw size={13} /> New chat
                </button>
              )}
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFA]">
              {messages.length === 0 && !streaming && (
                <div className="text-center mt-6">
                  <div className="w-12 h-12 rounded-full bg-ally-soft mx-auto flex items-center justify-center text-ally-pink mb-3"><Sparkles size={22} /></div>
                  <p className="text-ally-slate text-sm px-4">{isClient ? "Ask me about your deadlines, documents or any of your recommended listings. Your exchange team can see these questions." : exchangeId ? "I can see this whole exchange file. Ask me anything about it or its client, or name another exchange." : "Ask me about your clients, deadlines and exchanges. Name any client or exchange for the full details."}</p>
                  <div className="flex flex-col gap-2 mt-4">
                    {(isClient ? CLIENT_SUGGESTIONS : exchangeId ? EXCHANGE_SUGGESTIONS : SUGGESTIONS).map((s) => (
                      <button key={s} data-testid={`ally-suggest-${s.slice(0, 6)}`} onClick={() => send(s)}
                        className="bg-white border border-ally-soft hover:bg-ally-soft text-ally-ink text-xs font-medium rounded-full px-3 py-2 transition-colors">{s}</button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} data-testid={`ally-msg-${m.role}-${i}`} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                  <div className={m.role === "user"
                    ? "bg-ally-ink text-white rounded-2xl rounded-tr-md p-3 px-4 max-w-[85%] text-sm whitespace-pre-wrap"
                    : "bg-white text-ally-ink rounded-2xl rounded-tl-md p-3 px-4 max-w-[85%] border border-black/5 text-sm leading-relaxed"}>
                    {m.content ? (m.role === "assistant" ? <RichText text={parseAllyReply(m.content).visible || "I've prepared this for you to confirm:"} /> : m.content) : <span className="inline-flex gap-1"><span className="w-1.5 h-1.5 bg-ally-pink/60 rounded-full animate-bounce" /><span className="w-1.5 h-1.5 bg-ally-pink/60 rounded-full animate-bounce [animation-delay:0.15s]" /><span className="w-1.5 h-1.5 bg-ally-pink/60 rounded-full animate-bounce [animation-delay:0.3s]" /></span>}
                  </div>
                  {m.role === "assistant" && !(streaming && i === messages.length - 1) && parseAllyReply(m.content || "").actions.map((a, j) => (
                    <AllyActionCard key={j} action={a} testid={`ally-action-${i}-${j}`}
                      status={(m.actionStates || {})[j]}
                      onStatus={(st) => setMessages((ms) => ms.map((x, k) => (k === i ? { ...x, actionStates: { ...(x.actionStates || {}), [j]: st } } : x)))}
                      onOutcome={(note) => api.post(`/chat/${sid}/note`, { content: note }).catch(() => {})} />
                  ))}
                </div>
              ))}
            </div>
            <div className="border-t border-black/5 p-3">
              <div className="flex items-end gap-2 bg-[#FAFAFA] rounded-2xl p-1.5 pl-4">
                <textarea data-testid="ally-input" value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={1} placeholder="Ask Ally…" className="flex-1 bg-transparent outline-none resize-none py-2 text-sm text-ally-ink placeholder:text-ally-slate/60 max-h-24" />
                <button data-testid="ally-send" onClick={() => send()} disabled={streaming || !input.trim()}
                  className="bg-ally-pink text-white rounded-full w-9 h-9 flex items-center justify-center shrink-0 disabled:opacity-40 hover:bg-ally-pinkHover transition-colors"><Send size={16} /></button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
